import { useQuery } from "@tanstack/react-query";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Download, Trash2 } from "lucide-react";
import { supabase } from "@/lib/supabase";

interface DownloadEvent {
  id: string;
  user_email: string;
  content_title: string;
  action: "download" | "delete";
  file_count: number;
  created_at: string;
}

async function getDownloadEvents(): Promise<DownloadEvent[]> {
  if (!supabase) return [];
  const { data, error } = await supabase
    .from("download_events")
    .select("id, user_email, content_title, action, file_count, created_at")
    .order("created_at", { ascending: false })
    .limit(100);
  if (error) throw error;
  return data || [];
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("es", {
    day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

export default function DownloadsList() {
  const { data: events, isLoading } = useQuery({
    queryKey: ["download-events"],
    queryFn: getDownloadEvents,
  });

  const downloads = (events || []).filter((e) => e.action === "download");
  const totalDownloads = downloads.length;
  const uniqueUsers = new Set(downloads.map((e) => e.user_email)).size;

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">Historial de descargas offline realizadas por los usuarios desde la app móvil.</p>

      <div className="grid grid-cols-2 gap-4">
        <div className="rounded-lg border p-4">
          <p className="text-sm text-muted-foreground">Total descargas</p>
          <p className="text-2xl font-heading font-bold">{totalDownloads}</p>
        </div>
        <div className="rounded-lg border p-4">
          <p className="text-sm text-muted-foreground">Usuarios únicos</p>
          <p className="text-2xl font-heading font-bold">{uniqueUsers}</p>
        </div>
      </div>

      {isLoading ? (
        <p className="text-center py-8 text-muted-foreground">Cargando eventos...</p>
      ) : !events?.length ? (
        <div className="text-center py-16 text-muted-foreground">
          <Download className="h-12 w-12 mx-auto mb-4 opacity-30" />
          <p className="font-heading font-semibold mb-1">Sin descargas aún</p>
          <p className="text-sm">Cuando los usuarios descarguen contenido offline, aparecerá aquí.</p>
        </div>
      ) : (
        <div className="rounded-lg border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Usuario</TableHead>
                <TableHead>Contenido</TableHead>
                <TableHead>Acción</TableHead>
                <TableHead>Archivos</TableHead>
                <TableHead>Fecha</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {events.map((event) => (
                <TableRow key={event.id}>
                  <TableCell className="text-sm">{event.user_email || "—"}</TableCell>
                  <TableCell className="text-sm font-medium">{event.content_title}</TableCell>
                  <TableCell>
                    {event.action === "download" ? (
                      <Badge className="bg-primary/10 text-primary text-xs">
                        <Download className="h-3 w-3 mr-1" />Descarga
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-xs">
                        <Trash2 className="h-3 w-3 mr-1" />Eliminada
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{event.file_count || "—"}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{formatDate(event.created_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
